import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { LINKS } from '../constants/links';
import { apiUrl } from '../utils/apiConfig';
import { getToken, setToken as persistToken, setUser as persistUser } from '../utils/auth';
import { applyAppearance, readAppearance } from '../theme/appearance';
import {
  clearNotifications,
  formatNotifTime,
  markAllRead,
  markRead,
  noticeIconClass,
  noticeToneClass,
  openNoticeLink,
  unreadCount,
} from '../utils/notifications';
import { showToast } from '../utils/toast';
import { useMountTransition } from '../hooks/useMountTransition';
import BannerCarousel from './BannerCarousel';
import RoomMapModal from './RoomMapModal';
import NoticeDialog from './NoticeDialog';
import SmileLoader from './SmileLoader';
import { activateClose, dismissFocus, suppressGhostClick } from '../utils/dismissFocus';
import { clearSchedulePrefs, readSchedulePrefs } from '../utils/schedulePrefs';
import { openExternalUrl } from '../utils/openExternalUrl';
import './styles/Profile.css';

import bentoVk from '../assets/bento-vk-clear.webp';
import bentoSupport from '../assets/bento-support-clear.webp';
import bentoEo from '../assets/bento-eo-clear.webp';
import bentoCourses from '../assets/bento-courses-clear.webp';

function passwordStrength(value) {
  const v = value || '';
  if (!v) return 0;
  let pts = 0;
  if (v.length >= 6) pts += 1;
  if (v.length >= 10) pts += 1;
  if (/[A-Za-zА-Яа-я]/.test(v) && /\d/.test(v)) pts += 1;
  if (/[^A-Za-zА-Яа-я0-9]/.test(v)) pts += 1;
  return Math.min(3, pts);
}

function sessKindIcon(kind) {
  if (kind === 'phone') return 'bi-phone';
  if (kind === 'tablet') return 'bi-tablet';
  return 'bi-laptop';
}

function sessBrowserIcon(browser) {
  const b = String(browser || '').toLowerCase();
  if (b.includes('chrome')) return 'bi-browser-chrome';
  if (b.includes('edge')) return 'bi-browser-edge';
  if (b.includes('firefox')) return 'bi-browser-firefox';
  if (b.includes('safari')) return 'bi-browser-safari';
  if (b.includes('яндекс') || b.includes('yandex') || b.includes('opera')) return 'bi-globe2';
  return 'bi-globe2';
}

function sessDeviceTitle(item) {
  const d = String(item?.device || '').trim();
  if (d && d.toLowerCase() !== 'устройство') {
    return d.charAt(0).toUpperCase() + d.slice(1);
  }
  if (item?.kind === 'phone') return 'Телефон';
  if (item?.kind === 'tablet') return 'Планшет';
  return item?.browser || item?.label || 'Устройство';
}

function sessMetaLine(item) {
  const browser = String(item?.browser || '').trim();
  return browser || item?.label || 'Сессия';
}

function sessStatusLine(item) {
  return [item?.seen, item?.ip].filter(Boolean).join(' ') || 'Недавно';
}

function sessAppTone(app) {
  const a = String(app || '').toLowerCase();
  if (a.includes('расписан')) return 'schedule';
  if (a.includes('эо') || a.includes('обучен')) return 'eo';
  return 'app';
}

function sessAppIcon(app) {
  const tone = sessAppTone(app);
  if (tone === 'schedule') return 'bi-calendar3';
  if (tone === 'eo') return 'bi-journal-bookmark-fill';
  return 'bi-shield-lock';
}

const Profile = () => {
  const navigate = useNavigate();
  const {
    user,
    onLogout,
    onUserUpdate,
    notifications,
    setNotifications,
    openNotifications,
    openExternal,
  } = useOutletContext();
  const [dialog, setDialog] = useState(null); // 'account' | 'password' | 'notifs' | 'prefs' | 'sessions' | null
  const [dialogKind, setDialogKind] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [avatarSrc, setAvatarSrc] = useState('');
  const [form, setForm] = useState(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [emailDraft, setEmailDraft] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');
  const [pwd, setPwd] = useState({ old: '', next: '', confirm: '' });
  const [pwdShow, setPwdShow] = useState({ old: false, next: false, confirm: false });
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdMsg, setPwdMsg] = useState('');
  const [sessions, setSessions] = useState([]);
  const [sessBusy, setSessBusy] = useState(false);
  const [openedNotice, setOpenedNotice] = useState(null);
  const fileRef = useRef(null);
  const { mounted: dialogMounted, active: dialogActive } = useMountTransition(
    Boolean(dialog),
    320
  );

  useEffect(() => {
    if (dialog) setDialogKind(dialog);
    if (dialog === 'password') {
      setPwd({ old: '', next: '', confirm: '' });
      setPwdShow({ old: false, next: false, confirm: false });
      setPwdMsg('');
    }
  }, [dialog]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!dialogMounted) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setDialog(null);
    };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('kkt-dialog-open');
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('kkt-dialog-open');
      document.body.style.overflow = prev;
    };
  }, [dialogMounted]);

  const prefs = readSchedulePrefs();

  const displayName =
    user?.full_name ||
    user?.name ||
    user?.username ||
    localStorage.getItem('kkt_username') ||
    'Пользователь';

  const login =
    user?.username || localStorage.getItem('kkt_username') || '—';

  const unread = unreadCount(notifications || []);

  const prefsSummary = prefs.type
    ? prefs.type === 'student'
      ? prefs.group || 'Студент'
      : prefs.teacher || 'Преподаватель'
    : 'Не задан';

  const handleLogout = useCallback(() => {
    if (typeof onLogout === 'function') onLogout();
    navigate('/login', { replace: true });
  }, [onLogout, navigate]);

  const clearPrefs = () => {
    clearSchedulePrefs();
    setDialog(null);
    window.location.reload();
  };

  const confirmExternal = (url, label) => {
    if (typeof openExternal === 'function') {
      openExternal(url, label);
      return;
    }
    openExternalUrl(url);
  };

  const goSchedule = () => {
    setDialog(null);
    navigate('/schedule');
  };

  const closeDialog = (event) => {
    dismissFocus(event);
    suppressGhostClick(420);
    setDialog(null);
  };

  const authHeaders = () => {
    const token = getToken();
    if (!token || token === 'undefined') return {};
    return { Authorization: `Bearer ${token}` };
  };

  const applyUser = useCallback(
    (next) => {
      if (!next) return;
      if (typeof onUserUpdate === 'function') onUserUpdate(next);
      else {
        persistUser(next);
      }
    },
    [onUserUpdate]
  );

  useEffect(() => {
    if (!user?.has_avatar) {
      setAvatarSrc('');
      return undefined;
    }
    let revoked = false;
    let objectUrl = '';
    axios
      .get(apiUrl('/api/profile/avatar'), {
        headers: authHeaders(),
        responseType: 'blob',
        timeout: 8000,
      })
      .then((res) => {
        objectUrl = URL.createObjectURL(res.data);
        if (revoked) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setAvatarSrc(objectUrl);
      })
      .catch(() => {
        if (!revoked) setAvatarSrc('');
      });
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [user?.has_avatar, user?.id]);

  useEffect(() => {
    if (dialog !== 'account' || !user) return;
    const s = user.settings || {};
    const fields = Array.isArray(s.fields) ? s.fields : [];
    const next = {};
    fields.forEach((f) => {
      if (!f?.key) return;
      if (f.type === 'bool') {
        next[f.key] = s[f.key] != null ? Boolean(s[f.key]) : Boolean(f.default);
      } else if (f.key === 'theme') {
        const saved = s[f.key] === 'dark' || s[f.key] === 'light' ? s[f.key] : null;
        next[f.key] = saved || readAppearance();
      } else {
        next[f.key] = s[f.key] != null ? s[f.key] : f.default;
      }
    });
    setForm(next);
    setSaveMsg('');
    setPwd({ old: '', next: '', confirm: '' });
    setPwdMsg('');
  }, [dialog, user]);

  useEffect(() => {
    if (dialog !== 'email') return;
    setEmailDraft(user?.email || '');
    setEmailMsg('');
  }, [dialog, user]);

  useEffect(() => {
    if (dialog !== 'sessions') return undefined;
    let cancelled = false;
    setSessBusy(true);
    axios
      .get(apiUrl('/api/profile/sessions'), {
        headers: authHeaders(),
        timeout: 8000,
      })
      .then(({ data }) => {
        if (cancelled) return;
        setSessions(Array.isArray(data.items) ? data.items : []);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          handleLogout();
        }
      })
      .finally(() => {
        if (!cancelled) setSessBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dialog, handleLogout]);

  const applySessions = (data) => {
    if (!data) return;
    if (Array.isArray(data.items)) setSessions(data.items);
  };

  const revokeSession = (op, key) => {
    setSessBusy(true);
    axios
      .post(
        apiUrl('/api/profile/sessions'),
        { op, key },
        {
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          timeout: 8000,
        }
      )
      .then(({ data }) => {
        if (data?.current) {
          handleLogout();
          return;
        }
        applySessions(data);
        showToast('Готово', 'ok', op === 'revoke_others' ? 'Остальные входы закрыты' : 'Сессия завершена');
      })
      .catch((err) => {
        if (err?.response?.status === 401) handleLogout();
        else showToast('Не вышло', 'err', 'Не удалось закрыть сессию');
      })
      .finally(() => setSessBusy(false));
  };

  useEffect(() => {
    const refresh = () => {
      const token = getToken();
      if (!token) return;
      axios
        .get(apiUrl('/users/me'), {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 4000,
        })
        .then(({ data }) => applyUser(data))
        .catch(() => {});
    };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [applyUser]);

  const patchForm = (key, value) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const changePassword = async () => {
    if (pwd.next !== pwd.confirm) {
      setPwdMsg('Пароли не совпадают');
      return;
    }
    setPwdBusy(true);
    setPwdMsg('');
    try {
      const { data } = await axios.post(
        apiUrl('/api/profile/password'),
        {
          old_password: pwd.old,
          new_password: pwd.next,
          new_password2: pwd.confirm,
        },
        {
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          timeout: 8000,
        }
      );
      const nextToken = data?.access_token || data?.token;
      if (nextToken) {
        persistToken(nextToken);
      }
      setPwd({ old: '', next: '', confirm: '' });
      setPwdMsg('Пароль изменён');
      showToast('Готово', 'ok', 'Пароль изменён');
    } catch (err) {
      if (err?.response?.status === 401) {
        handleLogout();
        return;
      }
      setPwdMsg(err.response?.data?.detail || 'Не удалось сменить пароль');
    } finally {
      setPwdBusy(false);
    }
  };

  const sendEmail = async () => {
    setEmailBusy(true);
    setEmailMsg('');
    try {
      const { data } = await axios.post(
        apiUrl('/api/profile/email'),
        { email: emailDraft },
        {
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          timeout: 8000,
        }
      );
      applyUser(data);
      setDialog(null);
      showToast('Готово', 'ok', 'Почта сохранена');
    } catch (err) {
      setEmailMsg(err.response?.data?.detail || 'Не удалось сохранить почту');
    } finally {
      setEmailBusy(false);
    }
  };

  const saveSettings = async () => {
    if (!form) return;
    setSaveBusy(true);
    setSaveMsg('');
    try {
      const { data } = await axios.patch(apiUrl('/api/profile/settings'), form, {
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        timeout: 8000,
      });
      applyUser(data);
      if (form.theme) applyAppearance(form.theme);
      showToast('Готово', 'ok', 'Сохранены настройки');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Не удалось сохранить';
      setSaveMsg(detail);
      showToast('Ошибка', 'error', detail);
    } finally {
      setSaveBusy(false);
    }
  };

  const onPickPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPhotoBusy(true);
    setSaveMsg('');
    const body = new FormData();
    body.append('avatar', file);
    try {
      const { data } = await axios.post(apiUrl('/api/profile/avatar'), body, {
        headers: authHeaders(),
        timeout: 15000,
      });
      applyUser(data);
      showToast('Готово', 'ok', 'Фото обновлено');
    } catch (err) {
      setSaveMsg(err.response?.data?.detail || 'Не удалось загрузить фото');
    } finally {
      setPhotoBusy(false);
    }
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    setSaveMsg('');
    try {
      const { data } = await axios.delete(apiUrl('/api/profile/avatar'), {
        headers: authHeaders(),
        timeout: 8000,
      });
      applyUser(data);
      setAvatarSrc('');
      showToast('Готово', 'ok', 'Фото удалено');
    } catch (err) {
      setSaveMsg(err.response?.data?.detail || 'Не удалось удалить фото');
    } finally {
      setPhotoBusy(false);
    }
  };

  const settingsFields = user?.settings?.fields || [];
  const achievements = user?.achievements || [];
  const isStudentUser = (user?.role || 'student') === 'student';

  return (
    <div className="profile-page">
      <header className="profile-hero">
        <div className="profile-hero__glow" aria-hidden="true" />
        <div className={`profile-avatar${avatarSrc ? ' profile-avatar--photo' : ''}`} aria-hidden="true">
          {avatarSrc ? (
            <img src={avatarSrc} alt="" width={96} height={96} decoding="async" />
          ) : (
            (displayName || '?').charAt(0).toUpperCase()
          )}
        </div>
        <div className="profile-hero__meta">
          <h1 className="profile-hero__name">{displayName}</h1>
          <p className="profile-hero__login">{login}</p>
          <button
            type="button"
            className="profile-hero__email"
            onClick={() => setDialog('email')}
          >
            {user?.email || 'Указать почту'}
          </button>
        </div>
        <div className="profile-hero__actions">
          <button type="button" className="profile-hero__cta" onClick={goSchedule}>
            <i className="bi bi-calendar3" aria-hidden="true"></i>
            К расписанию
          </button>
          <button type="button" className="profile-logout profile-logout--hero" onClick={handleLogout}>
            Выйти
          </button>
        </div>
      </header>

      <div className="profile-desktop">
        <div className="profile-desktop__main">
          <section className={`profile-bento${isStudentUser ? '' : ' profile-bento--staff'}`} aria-label="Ссылки">
            <a
              className="profile-bento__card profile-bento__card--vk"
              href={LINKS.vkCommunity}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="profile-bento__art">
                <span className="profile-bento__fill" aria-hidden="true" />
                <img
                  className="profile-bento__mark"
                  src={bentoVk}
                  alt=""
                  width={192}
                  height={192}
                  decoding="async"
                />
              </span>
              <span className="profile-bento__wave" aria-hidden="true">
                <svg viewBox="0 0 400 48" preserveAspectRatio="none">
                  <path d="M0 18C50 6 85 36 130 22C175 8 210 34 260 20C310 8 350 30 400 16V48H0V18Z" />
                </svg>
              </span>
              <span className="profile-bento__body">
                <span className="profile-bento__title">ВКонтакте</span>
                <span className="profile-bento__text">
                  Новости техникума, объявления и общение
                </span>
                <span className="profile-bento__go">Открыть</span>
              </span>
            </a>

            <a
              className="profile-bento__card profile-bento__card--support"
              href={LINKS.vkSupport}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                event.preventDefault();
                confirmExternal(LINKS.vkSupport, 'поддержку ВКонтакте');
              }}
            >
              <span className="profile-bento__art">
                <span className="profile-bento__fill" aria-hidden="true" />
                <img
                  className="profile-bento__mark"
                  src={bentoSupport}
                  alt=""
                  width={160}
                  height={160}
                  decoding="async"
                />
              </span>
              <span className="profile-bento__wave" aria-hidden="true">
                <svg viewBox="0 0 400 48" preserveAspectRatio="none">
                  <path d="M0 28C56 12 96 40 148 24C200 8 236 38 288 22C340 8 368 34 400 18V48H0V28Z" />
                </svg>
              </span>
              <span className="profile-bento__body">
                <span className="profile-bento__title">Поддержка ВК</span>
                <span className="profile-bento__text">
                  Напишите по учёбе и приложению
                </span>
                <span className="profile-bento__go">Написать</span>
              </span>
            </a>

            <a
              className="profile-bento__card profile-bento__card--eo"
              href={LINKS.eo}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="profile-bento__art">
                <span className="profile-bento__fill" aria-hidden="true" />
                <img
                  className="profile-bento__mark"
                  src={bentoEo}
                  alt=""
                  width={160}
                  height={160}
                  decoding="async"
                />
              </span>
              <span className="profile-bento__wave" aria-hidden="true">
                <svg viewBox="0 0 400 48" preserveAspectRatio="none">
                  <path d="M0 20C60 36 100 8 152 24C204 40 248 10 304 26C360 40 380 16 400 30V48H0V20Z" />
                </svg>
              </span>
              <span className="profile-bento__body">
                <span className="profile-bento__title">Электронное обучение</span>
                <span className="profile-bento__text">
                  Электронное обучение и курсы
                </span>
                <span className="profile-bento__go">Перейти</span>
              </span>
            </a>

            {isStudentUser ? (
            <a
              className="profile-bento__card profile-bento__card--courses"
              href={LINKS.courses}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="profile-bento__art">
                <span className="profile-bento__fill" aria-hidden="true" />
                <img
                  className="profile-bento__mark"
                  src={bentoCourses}
                  alt=""
                  width={160}
                  height={160}
                  decoding="async"
                />
              </span>
              <span className="profile-bento__wave" aria-hidden="true">
                <svg viewBox="0 0 400 48" preserveAspectRatio="none">
                  <path d="M0 24C58 8 108 38 160 22C212 6 252 36 308 20C364 6 384 32 400 18V48H0V24Z" />
                </svg>
              </span>
              <span className="profile-bento__body">
                <span className="profile-bento__title">Доп. курсы</span>
                <span className="profile-bento__text">
                  IT-курсы техникума
                </span>
                <span className="profile-bento__go">Открыть</span>
              </span>
            </a>
            ) : null}
          </section>

          <BannerCarousel placement="profile" />
        </div>

        <aside className="profile-desktop__side">
          <div className="profile-settings" role="group" aria-label="Настройки">
            <button
              type="button"
              className="profile-settings__btn"
              onClick={() => setDialog('account')}
            >
              <span className="profile-settings__art profile-settings__art--account" aria-hidden="true">
                <span className="profile-settings__plate" />
                <span className="profile-settings__icon">
                  <i className="bi bi-person-gear"></i>
                </span>
              </span>
              <span className="profile-settings__meta">
                <span className="profile-settings__title">Настройки профиля</span>
                <span className="profile-settings__sub">
                  {isStudentUser ? 'Фото, приватность, достижения' : 'Фото, уведомления, тема'}
                </span>
              </span>
            </button>

            <button
              type="button"
              className="profile-settings__btn"
              onClick={() => setDialog('password')}
            >
              <span className="profile-settings__art profile-settings__art--password" aria-hidden="true">
                <span className="profile-settings__plate" />
                <span className="profile-settings__icon">
                  <i className="bi bi-key-fill"></i>
                </span>
              </span>
              <span className="profile-settings__meta">
                <span className="profile-settings__title">Пароль</span>
                <span className="profile-settings__sub">
                  {user?.vk_linked ? 'Смена · ВК привязан' : 'Смена · привязка ВК'}
                </span>
              </span>
            </button>

            <button
              type="button"
              className="profile-settings__btn"
              onClick={() => setDialog('notifs')}
            >
              <span className={`profile-settings__art ${unread > 0 ? 'is-live' : ''}`} aria-hidden="true">
                <span className="profile-settings__plate" />
                <span className="profile-settings__icon">
                  <i className="bi bi-bell-fill"></i>
                </span>
              </span>
              <span className="profile-settings__meta">
                <span className="profile-settings__title">Уведомления</span>
                <span className="profile-settings__sub">
                  {unread > 0
                    ? `${unread} новых`
                    : notifications?.length
                      ? 'Все прочитаны'
                      : 'Что приходит в колокольчик'}
                </span>
              </span>
            </button>

            <button
              type="button"
              className="profile-settings__btn"
              onClick={() => setDialog('prefs')}
            >
              <span className="profile-settings__art profile-settings__art--prefs" aria-hidden="true">
                <span className="profile-settings__plate" />
                <span className="profile-settings__icon">
                  <i className="bi bi-bookmark-heart-fill"></i>
                </span>
              </span>
              <span className="profile-settings__meta">
                <span className="profile-settings__title">Сохранённый выбор</span>
                <span className="profile-settings__sub">{prefsSummary}</span>
              </span>
            </button>

            <button
              type="button"
              className="profile-settings__btn"
              onClick={() => setDialog('sessions')}
            >
              <span className="profile-settings__art profile-settings__art--sessions" aria-hidden="true">
                <span className="profile-settings__plate" />
                <span className="profile-settings__icon">
                  <i className="bi bi-phone"></i>
                </span>
              </span>
              <span className="profile-settings__meta">
                <span className="profile-settings__title">Устройства и сессии</span>
                <span className="profile-settings__sub">Где выполнен вход</span>
              </span>
            </button>

            <button
              type="button"
              className="profile-settings__btn"
              onClick={() => confirmExternal(LINKS.vkCommunityDm, 'бота расписания ВКонтакте')}
            >
              <span className="profile-settings__art profile-settings__art--bot" aria-hidden="true">
                <span className="profile-settings__plate" />
                <span className="profile-settings__icon">
                  <i className="bi bi-robot"></i>
                </span>
              </span>
              <span className="profile-settings__meta">
                <span className="profile-settings__title">Расписание от бота</span>
                <span className="profile-settings__sub">Личка сообщества</span>
              </span>
            </button>

            <button
              type="button"
              className="profile-settings__btn"
              onClick={() => setMapOpen(true)}
            >
              <span className="profile-settings__art profile-settings__art--map" aria-hidden="true">
                <span className="profile-settings__plate" />
                <span className="profile-settings__icon">
                  <i className="bi bi-map-fill"></i>
                </span>
              </span>
              <span className="profile-settings__meta">
                <span className="profile-settings__title">Карта кабинетов</span>
                <span className="profile-settings__sub">1 и 2 этаж</span>
              </span>
            </button>
          </div>
        </aside>
      </div>

      <button type="button" className="profile-logout profile-logout--mobile" onClick={handleLogout}>
        Выйти
      </button>

      <RoomMapModal open={mapOpen} onClose={() => setMapOpen(false)} />
      <NoticeDialog
        open={Boolean(openedNotice)}
        notice={openedNotice}
        onClose={() => setOpenedNotice(null)}
        onOpenLink={(n) => {
          openNoticeLink(n.link, LINKS.eo);
          setOpenedNotice(null);
        }}
      />

      {dialogMounted && typeof document !== 'undefined'
        ? createPortal(
            <>
              <div
                className={`kkt-blur-backdrop${dialogActive ? ' is-visible' : ''}`}
                onClick={closeDialog}
                role="presentation"
              />
              <div
                className={`profile-sheet${dialogActive ? ' is-visible' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="profile-sheet-title"
              >
                <div className="profile-sheet__head">
                  <div className="profile-sheet__head-main">
                    <span
                      className={`profile-settings__icon ${
                        dialogKind === 'prefs'
                          ? 'profile-settings__icon--prefs'
                          : dialogKind === 'bot'
                            ? 'profile-settings__icon--bot'
                            : dialogKind === 'sessions'
                              ? 'profile-settings__icon--sessions'
                              : dialogKind === 'password'
                                ? 'profile-settings__icon--password'
                              : dialogKind === 'account'
                                ? 'profile-settings__icon--account'
                                : dialogKind === 'email'
                                  ? 'profile-settings__icon--account'
                                  : ''
                      } ${dialogKind === 'notifs' && unread > 0 ? 'is-live' : ''}`}
                      aria-hidden="true"
                    >
                      <i
                        className={`bi ${
                          dialogKind === 'notifs'
                            ? 'bi-bell-fill'
                            : dialogKind === 'bot'
                              ? 'bi-robot'
                              : dialogKind === 'sessions'
                                ? 'bi-phone'
                                : dialogKind === 'password'
                                  ? 'bi-key-fill'
                                : dialogKind === 'account'
                                  ? 'bi-person-gear'
                                  : dialogKind === 'email'
                                    ? 'bi-envelope'
                                    : 'bi-bookmark-heart-fill'
                        }`}
                      ></i>
                    </span>
                    <h2 id="profile-sheet-title">
                      {dialogKind === 'notifs'
                        ? 'Уведомления'
                        : dialogKind === 'bot'
                          ? 'Расписание от бота'
                          : dialogKind === 'sessions'
                            ? 'Устройства и сессии'
                            : dialogKind === 'password'
                              ? 'Пароль'
                            : dialogKind === 'account'
                              ? 'Настройки профиля'
                              : dialogKind === 'email'
                                ? 'Почта аккаунта'
                                : 'Сохранённый выбор'}
                    </h2>
                  </div>
                  <button
                    type="button"
                    className="profile-sheet__close"
                    onPointerDown={(event) => activateClose(event, () => setDialog(null))}
                    onClick={(event) => activateClose(event, () => setDialog(null))}
                    aria-label="Закрыть"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>

                <div className="profile-sheet__body">
                  {dialogKind === 'password' ? (
                    <div className="profile-account">
                      <div className="pass-vault">
                        <div
                          className="pass-vault__lock"
                          aria-hidden="true"
                          data-lvl={String(passwordStrength(pwd.next))}
                        >
                          <span className="pass-vault__shackle" />
                          <span className="pass-vault__body">
                            <i className="bi bi-shield-lock-fill" />
                          </span>
                        </div>
                        <div className="pass-vault__copy">
                          <strong>Новый ключ от аккаунта</strong>
                          <span>
                            Введите текущий пароль и новый. Старый сразу перестанет работать в ЭО и в
                            расписании.
                          </span>
                        </div>
                      </div>
                      <form
                        className="profile-account"
                        onSubmit={(event) => {
                          event.preventDefault();
                          changePassword();
                        }}
                      >
                        <label className="profile-field pass-field">
                          <span>Текущий пароль</span>
                          <span className="pass-field__box">
                            <input
                              type={pwdShow.old ? 'text' : 'password'}
                              value={pwd.old}
                              onChange={(event) => setPwd((prev) => ({ ...prev, old: event.target.value }))}
                              autoComplete="current-password"
                              required
                            />
                            <button
                              type="button"
                              className={`pass-eye${pwdShow.old ? ' is-on' : ''}`}
                              aria-label={pwdShow.old ? 'Скрыть пароль' : 'Показать пароль'}
                              aria-pressed={pwdShow.old}
                              onClick={() => setPwdShow((p) => ({ ...p, old: !p.old }))}
                            >
                              <i className="bi bi-eye" aria-hidden="true" />
                              <i className="bi bi-eye-slash" aria-hidden="true" />
                            </button>
                          </span>
                        </label>
                        <label className="profile-field pass-field">
                          <span>Новый пароль</span>
                          <span className="pass-field__box">
                            <input
                              type={pwdShow.next ? 'text' : 'password'}
                              value={pwd.next}
                              onChange={(event) => setPwd((prev) => ({ ...prev, next: event.target.value }))}
                              autoComplete="new-password"
                              minLength={6}
                              required
                            />
                            <button
                              type="button"
                              className={`pass-eye${pwdShow.next ? ' is-on' : ''}`}
                              aria-label={pwdShow.next ? 'Скрыть пароль' : 'Показать пароль'}
                              aria-pressed={pwdShow.next}
                              onClick={() => setPwdShow((p) => ({ ...p, next: !p.next }))}
                            >
                              <i className="bi bi-eye" aria-hidden="true" />
                              <i className="bi bi-eye-slash" aria-hidden="true" />
                            </button>
                          </span>
                        </label>
                        {pwd.next ? (
                          <div className="pass-meter">
                            <div className="pass-meter__track">
                              <i
                                style={{
                                  width: `${
                                    passwordStrength(pwd.next) <= 1
                                      ? 34
                                      : passwordStrength(pwd.next) === 2
                                        ? 66
                                        : 100
                                  }%`,
                                }}
                                data-lvl={String(passwordStrength(pwd.next))}
                              />
                            </div>
                            <span>
                              {passwordStrength(pwd.next) <= 1
                                ? 'Слабый, лучше длиннее'
                                : passwordStrength(pwd.next) === 2
                                  ? 'Нормально'
                                  : 'Надёжный'}
                            </span>
                          </div>
                        ) : null}
                        <label className="profile-field pass-field">
                          <span>Ещё раз новый</span>
                          <span className="pass-field__box">
                            <input
                              type={pwdShow.confirm ? 'text' : 'password'}
                              value={pwd.confirm}
                              onChange={(event) =>
                                setPwd((prev) => ({ ...prev, confirm: event.target.value }))
                              }
                              autoComplete="new-password"
                              minLength={6}
                              required
                            />
                            <button
                              type="button"
                              className={`pass-eye${pwdShow.confirm ? ' is-on' : ''}`}
                              aria-label={pwdShow.confirm ? 'Скрыть пароль' : 'Показать пароль'}
                              aria-pressed={pwdShow.confirm}
                              onClick={() => setPwdShow((p) => ({ ...p, confirm: !p.confirm }))}
                            >
                              <i className="bi bi-eye" aria-hidden="true" />
                              <i className="bi bi-eye-slash" aria-hidden="true" />
                            </button>
                          </span>
                        </label>
                        {pwd.confirm ? (
                          <p
                            className={`pass-match${
                              pwd.next && pwd.next === pwd.confirm ? ' is-ok' : ' is-bad'
                            }`}
                          >
                            {pwd.next && pwd.next === pwd.confirm
                              ? 'Совпадают'
                              : 'Пока не совпадают'}
                          </p>
                        ) : null}
                        <div className="profile-account__save profile-account__save--pass">
                          <p className="profile-pass-recover">
                            Забыли пароль?
                            <br />
                            <a
                              href={LINKS.vkCommunityDm}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Восстановите
                            </a>
                          </p>
                          <button
                            type="submit"
                            className="profile-panel__cta"
                            disabled={pwdBusy}
                          >
                            {pwdBusy ? (
                              <>
                                <SmileLoader size="xs" />
                                <span>Сохраняем…</span>
                              </>
                            ) : (
                              'Сохранить пароль'
                            )}
                          </button>
                          {pwdMsg ? <span className="profile-account__msg">{pwdMsg}</span> : null}
                        </div>
                      </form>
                      {user?.vk_linked ? (
                        <button
                          type="button"
                          className="vk-link-banner vk-link-banner--compact"
                          onClick={() => {
                            setDialog(null);
                            navigate('/profile/vk');
                          }}
                        >
                          <span className="vk-link-banner__logo" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12.785 16.241s.288-.032.436-.194c.136-.148.132-.427.132-.427s-.02-1.304.587-1.496c.598-.19 1.367 1.26 2.182 1.818.616.422 1.084.33 1.084.33l2.177-.03s1.138-.071.598-.964c-.044-.073-.314-.661-1.618-1.869-1.365-1.263-1.182-1.058.462-3.241.999-1.328 1.399-2.14 1.273-2.486-.12-.33-.859-.243-.859-.243l-2.451.015s-.182-.025-.316.056c-.13.079-.214.263-.214.263s-.385 1.023-.897 1.893c-1.08 1.834-1.513 1.931-1.69 1.818-.412-.264-.309-1.06-.309-1.626 0-1.768.268-2.503-.522-2.695-.263-.064-.456-.106-1.127-.113-.861-.009-1.59.003-2.003.206-.275.135-.487.435-.358.452.16.021.522.098.714.38.247.364.238 1.182.238 1.182s.142 2.25-.33 2.529c-.324.191-.769-.199-1.725-1.845-.489-.84-.859-1.77-.859-1.77s-.071-.174-.198-.267c-.154-.113-.37-.149-.37-.149l-2.328.015s-.35.01-.478.162c-.114.135-.009.414-.009.414s1.805 4.219 3.847 6.342c1.872 1.947 3.995 1.819 3.995 1.819h.963z" />
                            </svg>
                          </span>
                          <span className="vk-link-banner__copy">
                            <strong>ВКонтакте</strong>
                            <em>Аккаунт связан</em>
                          </span>
                          <span className="vk-link-banner__go" aria-hidden="true">
                            <i className="bi bi-chevron-right" />
                          </span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="vk-link-banner vk-link-banner--compact"
                          onClick={() => {
                            setDialog(null);
                            navigate('/profile/vk');
                          }}
                        >
                          <span className="vk-link-banner__logo" aria-hidden="true">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12.785 16.241s.288-.032.436-.194c.136-.148.132-.427.132-.427s-.02-1.304.587-1.496c.598-.19 1.367 1.26 2.182 1.818.616.422 1.084.33 1.084.33l2.177-.03s1.138-.071.598-.964c-.044-.073-.314-.661-1.618-1.869-1.365-1.263-1.182-1.058.462-3.241.999-1.328 1.399-2.14 1.273-2.486-.12-.33-.859-.243-.859-.243l-2.451.015s-.182-.025-.316.056c-.13.079-.214.263-.214.263s-.385 1.023-.897 1.893c-1.08 1.834-1.513 1.931-1.69 1.818-.412-.264-.309-1.06-.309-1.626 0-1.768.268-2.503-.522-2.695-.263-.064-.456-.106-1.127-.113-.861-.009-1.59.003-2.003.206-.275.135-.487.435-.358.452.16.021.522.098.714.38.247.364.238 1.182.238 1.182s.142 2.25-.33 2.529c-.324.191-.769-.199-1.725-1.845-.489-.84-.859-1.77-.859-1.77s-.071-.174-.198-.267c-.154-.113-.37-.149-.37-.149l-2.328.015s-.35.01-.478.162c-.114.135-.009.414-.009.414s1.805 4.219 3.847 6.342c1.872 1.947 3.995 1.819 3.995 1.819h.963z" />
                            </svg>
                          </span>
                          <span className="vk-link-banner__copy">
                            <strong>Привязать ВКонтакте</strong>
                            <em>Нужно для восстановления пароля и расписания в боте</em>
                          </span>
                          <span className="vk-link-banner__go" aria-hidden="true">
                            <i className="bi bi-chevron-right" />
                          </span>
                        </button>
                      )}
                    </div>
                  ) : dialogKind === 'sessions' ? (
                    <div className="sess">
                      {sessBusy && !sessions.length ? (
                        <div className="profile-panel__empty">
                          <SmileLoader size="xs" />
                        </div>
                      ) : (
                        <>
                          {sessions.filter((s) => s.current).map((item) => (
                            <div key={item.key} className="sess-now">
                              <div className="sess-now__label">Это устройство</div>
                              <article className="sess-card sess-card--now">
                                <span className={`sess-ico sess-ico--${item.kind || 'desktop'}`} aria-hidden="true">
                                  <span className="sess-ico__dot" title="Сейчас активна" />
                                  <i className={`bi ${sessKindIcon(item.kind)}`} />
                                </span>
                                <div className="sess-card__copy">
                                  <div className="sess-card__title">
                                    <strong>{sessDeviceTitle(item)}</strong>
                                  </div>
                                  <div className="sess-card__meta">
                                    <i className={`bi ${sessBrowserIcon(item.browser)}`} aria-hidden="true" />
                                    <span>{sessMetaLine(item)}</span>
                                  </div>
                                  {item.app ? (
                                    <div className="sess-card__service">
                                      <em className={`sess-app sess-app--${sessAppTone(item.app)}`}>
                                        <i className={`bi ${sessAppIcon(item.app)}`} aria-hidden="true" />
                                        {item.app}
                                      </em>
                                    </div>
                                  ) : null}
                                  <div className="sess-card__status">
                                    <span>
                                      Сейчас активна
                                      {item.ip ? ` ${item.ip}` : ''}
                                    </span>
                                  </div>
                                </div>
                              </article>
                            </div>
                          ))}
                          {(() => {
                            const others = sessions.filter((s) => !s.current);
                            if (!others.length) return null;
                            return (
                              <>
                                <h3 className="sess__section">
                                  <span className="sess__section-count">{others.length}</span>
                                  Другие устройства
                                </h3>
                                <div className="sess__block">
                                  <ul className="sess__list">
                                    {others.map((item) => (
                                      <li key={item.key} className="sess-card">
                                        <span className={`sess-ico sess-ico--${item.kind || 'desktop'}`} aria-hidden="true">
                                          <i className={`bi ${sessKindIcon(item.kind)}`} />
                                        </span>
                                        <div className="sess-card__copy">
                                          <div className="sess-card__title">
                                            <strong>{sessDeviceTitle(item)}</strong>
                                          </div>
                                          <div className="sess-card__meta">
                                            <i className={`bi ${sessBrowserIcon(item.browser)}`} aria-hidden="true" />
                                            <span>{sessMetaLine(item)}</span>
                                          </div>
                                          {item.app ? (
                                            <div className="sess-card__service">
                                              <em className={`sess-app sess-app--${sessAppTone(item.app)}`}>
                                                <i className={`bi ${sessAppIcon(item.app)}`} aria-hidden="true" />
                                                {item.app}
                                              </em>
                                            </div>
                                          ) : null}
                                          <div className="sess-card__status">
                                            <span>{sessStatusLine(item)}</span>
                                          </div>
                                        </div>
                                        <button
                                          type="button"
                                          className="sess-card__end"
                                          disabled={sessBusy}
                                          onClick={() => revokeSession('revoke', item.key)}
                                        >
                                          Выйти
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                  <button
                                    type="button"
                                    className="sess__flush-btn"
                                    disabled={sessBusy}
                                    onClick={() => revokeSession('revoke_others')}
                                  >
                                    <svg className="sess__flush-btn__hand" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                      <path d="M23 5.5V20c0 2.2-1.8 4-4 4h-7.3c-1.08 0-2.1-.43-2.85-1.19L1 14.83s1.26-1.23 1.3-1.25c.22-.19.49-.29.79-.29.22 0 .42.06.6.16.04.01 4.31 2.46 4.31 2.46V4c0-.83.67-1.5 1.5-1.5S11 3.17 11 4v7h1V1.5c0-.83.67-1.5 1.5-1.5S15 .67 15 1.5V11h1V2.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V11h1V5.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5z" />
                                    </svg>
                                    Завершить остальные
                                  </button>
                                </div>
                              </>
                            );
                          })()}
                          {!sessions.length ? (
                            <div className="sess-card sess-card--empty">
                              <strong>Активных сессий нет</strong>
                              <span>Войдите с другого устройства — оно появится здесь</span>
                            </div>
                          ) : null}
                        </>
                      )}
                    </div>
                  ) : dialogKind === 'email' ? (
                    <form
                      className="profile-email"
                      onSubmit={(event) => {
                        event.preventDefault();
                        sendEmail();
                      }}
                    >
                      <p className="profile-email__text">
                        Почта для уведомлений. Восстановление пароля — через бота ВК.
                      </p>
                      <label className="profile-field">
                        <span>Почта</span>
                        <input
                          type="email"
                          value={emailDraft}
                          onChange={(event) => setEmailDraft(event.target.value)}
                          autoComplete="email"
                          placeholder="name@example.com"
                          required
                        />
                      </label>
                      <div className="profile-account__save">
                        <button
                          type="submit"
                          className="profile-panel__cta"
                          disabled={emailBusy}
                        >
                          {emailBusy ? (
                            <>
                              <SmileLoader size="xs" />
                              <span>Сохраняем…</span>
                            </>
                          ) : user?.email ? (
                            'Заменить почту'
                          ) : (
                            'Сохранить почту'
                          )}
                        </button>
                        {emailMsg ? <span className="profile-account__msg">{emailMsg}</span> : null}
                      </div>
                    </form>
                  ) : dialogKind === 'account' ? (
                    <div className="profile-account">
                      <div className="profile-account__photo">
                        <div className={`profile-account__preview${avatarSrc ? ' is-photo' : ''}`}>
                          {avatarSrc ? (
                            <img src={avatarSrc} alt="" width={72} height={72} decoding="async" />
                          ) : (
                            (displayName || '?').charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className="profile-account__photo-meta">
                          <strong>Фото профиля</strong>
                          <span>JPG, PNG или WebP. Сжимаем до квадрата WebP, чтобы быстрее грузилось и в ЭО, и здесь.</span>
                          <div className="profile-account__photo-actions">
                            <button
                              type="button"
                              className="profile-panel__cta"
                              disabled={photoBusy}
                              onClick={() => fileRef.current?.click()}
                            >
                              {photoBusy ? (
                                <>
                                  <SmileLoader size="xs" />
                                  <span>Загрузка…</span>
                                </>
                              ) : (
                                'Сменить фото'
                              )}
                            </button>
                            {user?.has_avatar ? (
                              <button
                                type="button"
                                className="profile-prefs__reset"
                                disabled={photoBusy}
                                onClick={removePhoto}
                              >
                                Удалить
                              </button>
                            ) : null}
                          </div>
                          <input
                            ref={fileRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            hidden
                            onChange={onPickPhoto}
                          />
                        </div>
                      </div>

                      {form ? (
                        <>
                          {settingsFields.map((field) => {
                            if (field.type === 'bool') {
                              return (
                                <label className="profile-toggle" key={field.key}>
                                  <span>
                                    <strong>{field.label}</strong>
                                    {field.hint ? (
                                      <span className="profile-toggle__hint">{field.hint}</span>
                                    ) : null}
                                  </span>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(form[field.key])}
                                    onChange={(e) => patchForm(field.key, e.target.checked)}
                                  />
                                </label>
                              );
                            }
                            if (field.type === 'choice' && field.widget === 'segmented') {
                              return (
                                <div className="profile-theme" key={field.key}>
                                  <span className="profile-theme__label">{field.label}</span>
                                  <div
                                    className="profile-theme__switch"
                                    role="radiogroup"
                                    aria-label={field.label}
                                  >
                                    {(field.choices || []).map((opt) => (
                                      <button
                                        type="button"
                                        role="radio"
                                        aria-checked={form[field.key] === opt.value}
                                        key={opt.value}
                                        className={form[field.key] === opt.value ? 'is-on' : ''}
                                        onClick={() => {
                                          patchForm(field.key, opt.value);
                                          if (field.key === 'theme') {
                                            applyAppearance(opt.value);
                                            applyUser({
                                              ...user,
                                              settings: { ...(user?.settings || {}), theme: opt.value },
                                            });
                                            axios
                                              .patch(
                                                apiUrl('/api/profile/settings'),
                                                { theme: opt.value },
                                                {
                                                  headers: {
                                                    ...authHeaders(),
                                                    'Content-Type': 'application/json',
                                                  },
                                                  timeout: 8000,
                                                }
                                              )
                                              .then(({ data }) => {
                                                applyUser(data);
                                                applyAppearance(opt.value);
                                              })
                                              .catch(() => {});
                                          }
                                        }}
                                      >
                                        {opt.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            }
                            if (field.type === 'choice') {
                              return (
                                <label className="profile-field" key={field.key}>
                                  <span>{field.label}</span>
                                  <select
                                    value={form[field.key] || field.default || ''}
                                    onChange={(e) => {
                                      patchForm(field.key, e.target.value);
                                      if (field.key === 'theme') applyAppearance(e.target.value);
                                    }}
                                  >
                                    {(field.choices || []).map((opt) => (
                                      <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              );
                            }
                            return null;
                          })}

                          <div className="profile-account__save">
                            <button
                              type="button"
                              className="profile-panel__cta"
                              disabled={saveBusy}
                              onClick={saveSettings}
                            >
                              {saveBusy ? (
                                <>
                                  <SmileLoader size="xs" />
                                  <span>Сохранение…</span>
                                </>
                              ) : (
                                'Сохранить'
                              )}
                            </button>
                            {saveMsg ? <span className="profile-account__msg">{saveMsg}</span> : null}
                          </div>
                        </>
                      ) : null}

                      {isStudentUser ? (
                      <div className="profile-achievements">
                        <div className="profile-achievements__head">
                          <strong>Достижения ЭО</strong>
                          {user?.stats?.rating != null ? (
                            <span>Рейтинг {user.stats.rating}</span>
                          ) : null}
                        </div>
                        {achievements.length ? (
                          <ul className="profile-achievements__list">
                            {achievements.map((item) => (
                              <li key={item.id} className="profile-achievement">
                                <span
                                  className="profile-achievement__icon"
                                  style={{ color: item.color || '#4caf45' }}
                                  aria-hidden="true"
                                >
                                  <i className={`bi ${item.icon || 'bi-trophy'}`}></i>
                                </span>
                                <span className="profile-achievement__body">
                                  <strong>{item.title}</strong>
                                  {item.description ? <span>{item.description}</span> : null}
                                </span>
                                {item.points ? (
                                  <em className="profile-achievement__pts">+{item.points}</em>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="profile-achievements__empty">
                            Пока пусто — значки появятся за тесты, ДЗ и активность в ЭО
                          </p>
                        )}
                      </div>
                      ) : null}
                    </div>
                  ) : dialogKind === 'notifs' ? (
                    <>
                      {(!notifications || notifications.length === 0) ? null : (
                        <div className="profile-sheet__toolbar">
                          {unread > 0 ? (
                            <button
                              type="button"
                              className="profile-panel__icon-btn"
                              onClick={() => {
                                setNotifications(markAllRead(notifications));
                                showToast('Прочитано', 'ok', 'Все уведомления отмечены');
                              }}
                              aria-label="Прочитать все"
                              title="Прочитать все"
                            >
                              <i className="bi bi-check2-all" aria-hidden="true" />
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="profile-panel__icon-btn profile-panel__icon-btn--danger"
                            onClick={() => {
                              setNotifications(clearNotifications());
                              showToast('Очищено', 'ok', 'Список уведомлений пуст');
                            }}
                            aria-label="Очистить"
                            title="Очистить"
                          >
                            <i className="bi bi-trash" aria-hidden="true" />
                          </button>
                        </div>
                      )}

                      {(!notifications || notifications.length === 0) ? (
                        <div className="profile-panel__empty">
                          <span className="profile-panel__empty-art" aria-hidden="true">
                            <i className="bi bi-moon-stars"></i>
                          </span>
                          <p className="profile-panel__empty-title">Пока пусто</p>
                          <p className="profile-panel__empty-text">
                            Здесь появятся изменения расписания и важные новости
                          </p>
                          <button
                            type="button"
                            className="profile-panel__cta"
                            onClick={() => {
                              closeDialog();
                              openNotifications?.();
                            }}
                          >
                            Открыть панель
                          </button>
                        </div>
                      ) : (
                        <ul className="notif-list profile-notif-list">
                          {notifications.slice(0, 12).map((n) => (
                            <li key={n.id}>
                              <button
                                type="button"
                                className={`kkt-notice notif-plate ${noticeToneClass(n.level, n.type)} ${n.read ? 'is-read' : 'is-unread'}`}
                                onClick={() => {
                                  setNotifications(markRead(notifications, n.id));
                                  setOpenedNotice(n);
                                }}
                              >
                                <span className="kkt-notice__ava" aria-hidden="true">
                                  <i className={`bi ${noticeIconClass(n.icon, n.type, n.level)}`} />
                                </span>
                                <span className="kkt-notice__copy">
                                  <span className="kkt-notice__head">
                                    <strong>{n.title}</strong>
                                    <time>{formatNotifTime(n.createdAt)}</time>
                                  </span>
                                  {n.body ? <span className="kkt-notice__text">{n.body}</span> : null}
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : prefs.type ? (
                    <div className="profile-prefs">
                      <div className="profile-prefs__card">
                        <div className="profile-prefs__glow" aria-hidden="true" />
                        <div className="profile-prefs__role">
                          <span className="profile-prefs__role-icon" aria-hidden="true">
                            <i
                              className={`bi ${
                                prefs.type === 'student'
                                  ? 'bi-mortarboard-fill'
                                  : 'bi-person-workspace'
                              }`}
                            ></i>
                          </span>
                          <div>
                            <span className="profile-prefs__label">Тип</span>
                            <strong>
                              {prefs.type === 'student' ? 'Студент' : 'Преподаватель'}
                            </strong>
                          </div>
                        </div>

                        {prefs.type === 'student' && prefs.group ? (
                          <div className="profile-prefs__value">
                            <span className="profile-prefs__label">Группа</span>
                            <span className="profile-prefs__big">{prefs.group}</span>
                          </div>
                        ) : null}

                        {prefs.type === 'teacher' && prefs.teacher ? (
                          <div className="profile-prefs__value">
                            <span className="profile-prefs__label">Преподаватель</span>
                            <span className="profile-prefs__big profile-prefs__big--sm">
                              {prefs.teacher}
                            </span>
                          </div>
                        ) : null}
                      </div>

                      <div className="profile-prefs__actions">
                        <button type="button" className="profile-panel__cta" onClick={goSchedule}>
                          Открыть расписание
                        </button>
                        <button type="button" className="profile-prefs__reset" onClick={clearPrefs}>
                          Сбросить
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="profile-panel__empty profile-panel__empty--prefs">
                      <span className="profile-panel__empty-art" aria-hidden="true">
                        <i className="bi bi-bookmark-plus"></i>
                      </span>
                      <p className="profile-panel__empty-title">Ничего не сохранено</p>
                      <p className="profile-panel__empty-text">
                        В расписании отметьте «Запомнить мой выбор» — группа или преподаватель
                        подставятся сами
                      </p>
                      <button type="button" className="profile-panel__cta" onClick={goSchedule}>
                        К расписанию
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>,
            document.body
          )
        : null}
    </div>
  );
};

export default Profile;
